import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private readonly VERSION_URL = `${window.location.origin}/assets/version.json`;
  private readonly CHECK_INTERVAL = 30000; // 30 seconds
  private readonly BACKOFF_INTERVAL = 300000; // 5 minutes
  // Current version hash from version.json
  private currentHash = '';
  private checkSubscription: Subscription | null = null;
  private hasNotifiedUser = false;

  private updateAvailable = new BehaviorSubject<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public updateAvailable$ = this.updateAvailable.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Start checking for version updates
   */
  public startVersionCheck(): void {
    // Get initial hash from version.json
    this.fetchVersionHash().subscribe((hash) => {
      if (hash) {
        this.currentHash = hash;
        this.startPeriodicCheck();
        return;
      }
      // eslint-disable-next-line no-console
      console.warn('No initial hash received');
    });
  }

  /**
   * Stop checking for version updates
   */
  public stopVersionCheck(): void {
    if (this.checkSubscription) {
      this.checkSubscription.unsubscribe();
      this.checkSubscription = null;
    }
  }

  public fetchVersionHash(): Observable<string> {
    // Add cache-busting query parameter
    const url = `${this.VERSION_URL}?t=${Date.now()}`;
    return this.http.get<{ hash: string }>(url, {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }
    }).pipe(
      map((response) => response.hash),
      catchError(() => new Observable<string>())
    );
  }

  public getCurrentHash(): string {
    return this.currentHash;
  }

  private startPeriodicCheck(): void {
    // Stop any existing subscription
    this.stopVersionCheck();

    const checkInterval = interval(this.hasNotifiedUser ? this.BACKOFF_INTERVAL : this.CHECK_INTERVAL);

    this.checkSubscription = checkInterval.pipe(
      switchMap(() => this.fetchVersionHash())
    ).subscribe((newHash) => {

      const isFirstFetchedHash = this.currentHash === '';
      if (isFirstFetchedHash) {
        this.currentHash = newHash;
      }
      // Only notify if we have both hashes and they don't match
      const isHashUpdated = newHash && this.currentHash && newHash !== this.currentHash;
      if (isHashUpdated) {

          this.hasNotifiedUser = true;
          this.updateAvailable.next(true);
          this.startPeriodicCheck(); // Restart with longer interval

      }
    });
  }
}
